/* THE CARD A COACH'S LIBRARY PROPOSAL BECOMES (utils/coachLibrary.js).
 *
 * Under the coach's reply, the same dashed card shape as "Ready to lock", so a
 * thing waiting on her word reads the same everywhere. It SHOWS the item — the
 * block itself is stripped from the chat, and she cannot be asked to keep
 * something she has not been shown. Save writes it through the one Library
 * store and opens the Library on it, as Save that thought does. Keep chatting
 * just closes the card.
 *
 * Never a silent failure: a save that does not land says so in amber and keeps
 * the card. A card that comes back after a refresh, for an item she already
 * saved, opens that item rather than saving it twice.
 */
import { useState } from "react";
import { BookmarkPlus } from "lucide-react";
import { useLibrary, kindOf } from "../utils/library";
import { alreadySaved } from "../utils/coachLibrary";

export function LibraryProposal({ item, onSaved, onDismiss, isMobile }) {
  const { rows, save } = useLibrary();
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");
  const kind = kindOf(item.kind);

  const keep = async () => {
    setProblem("");
    const have = alreadySaved(rows, item);
    if (have) { onSaved(have); return; }
    setSaving(true);
    const saved = await save(item);
    setSaving(false);
    if (!saved) { setProblem("That didn't save. It's still here — try again."); return; }
    onSaved(saved);
  };

  return (
    <div style={{
      margin: "2px 0 12px", marginLeft: 38,
      border: "1px dashed var(--border-strong)", background: "var(--bg-surface)",
      borderRadius: 12, padding: "12px 14px",
    }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 7 }}>
        For your Library · {kind.label}
      </div>
      {item.title && <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 5 }}>{item.title}</div>}
      <div style={{ fontSize: isMobile ? 13.5 : 12.5, lineHeight: 1.65, color: "var(--text-secondary)", whiteSpace: "pre-wrap", maxHeight: 260, overflowY: "auto" }}>
        {item.text}
      </div>
      {item.answer && (
        <div style={{ fontSize: isMobile ? 13.5 : 12.5, lineHeight: 1.65, color: "var(--text-secondary)", whiteSpace: "pre-wrap", marginTop: 8 }}>
          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>Her answer: </span>{item.answer}
        </div>
      )}
      {problem && <div role="status" style={{ marginTop: 8, fontSize: 12.5, color: "var(--warn-text)" }}>{problem}</div>}
      <div style={{ display: "flex", gap: 7, marginTop: 11, flexWrap: "wrap" }}>
        <button onClick={keep} disabled={saving}
          style={{
            padding: "6px 13px", borderRadius: 7, border: "none",
            background: "var(--btn-bg)", color: "var(--btn-text)",
            fontSize: 12, fontWeight: 600, cursor: saving ? "wait" : "pointer", fontFamily: "inherit",
            display: "flex", alignItems: "center", gap: 5,
          }}>
          <BookmarkPlus size={12} /> {saving ? "Saving…" : "Save to my Library"}
        </button>
        <button onClick={onDismiss}
          style={{
            padding: "6px 13px", borderRadius: 7,
            border: "1px solid var(--border-strong)", background: "var(--bg-surface)",
            color: "var(--text-secondary)", fontSize: 12, fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit",
          }}>
          Keep chatting
        </button>
      </div>
    </div>
  );
}
